import React, {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import './MatchPolicyToast.css';
import PolicyPurchaseToast from '../PolicyPurchaseToast/PolicyPurchaseToast';
import {hidePolicyPurchaseToast} from '../../redux/actions';
import {selectPolicyPurchase} from '../../redux/reducers/privateStateReducer/selectors';
import {selectPlayers} from '../../redux/reducers/sharedStateReducer/selectors';
import {usePausableTimeout} from '../../hooks/react/usePausableTimeout';
import {useMatchToastsPaused} from '../MatchToastStack/matchToastsPause';

// Столько же, сколько висел прежний текстовый тост о покупке политики
const MATCH_POLICY_TOAST_DURATION_MS = 3000;
// Совпадает с transition в MatchPolicyToast.css: столько тост выезжает и
// столько же уезжает обратно за правый край
const SLIDE_DURATION_MS = 300;

/**
 * Тост «игрок купил политику» в матче: элемент общего стека тостов
 * (.toast-stack в GameContent), поэтому позиция и отступ до соседних тостов
 * считаются браузером. Сама плашка — общий PolicyPurchaseToast, такая же
 * показывается на витрине главного меню.
 *
 * Покупка кладется в приватный стейт из playEffectForServerAction; звук
 * покупки играет он же, здесь только показ и снятие.
 *
 * Тост выезжает из края стека (MatchPolicyToast.css), висит
 * MATCH_POLICY_TOAST_DURATION_MS и уезжает обратно: покупка убирается из
 * стейта (а с ней и сам элемент) только после того, как отработал обратный
 * переход, — иначе тост пропадал бы мгновенно, посреди кадра.
 *
 * На паузе уведомлений (на телефоне открыт список игроков или рынок) тоста не
 * видно и вся цепочка стоит: покупка, пришедшая на паузе, выезжает и висит
 * свои 3 с уже после нее.
 */
const MatchPolicyToast = () => {
  const dispatch = useDispatch();
  const purchase = useSelector(selectPolicyPurchase);
  const players = useSelector(selectPlayers);

  // Покупка, лежавшая в стейте ДО монтирования, к этому матчу не относится:
  // витрина главного меню крутит реплей через тот же
  // playEffectForServerAction и оставляет там последнюю покупку из записи.
  // Без этого матч (или просмотр записи) открывался бы с чужим тостом
  const stalePurchaseIdRef = useRef(purchase ? purchase.id : null);
  const isStale = Boolean(purchase) && purchase.id === stalePurchaseIdRef.current;

  const purchaseId = purchase && !isStale ? purchase.id : null;

  const [isVisible, setIsVisible] = useState(false);
  const isPaused = useMatchToastsPaused();

  useEffect(() => {
    setIsVisible(false);
  }, [purchaseId]);

  // Цепочка от появления покупки: все три таймера стоят на паузе вместе.
  // Появление через кадр: элемент должен смонтироваться сдвинутым, иначе
  // CSS-переход не сработает и тост просто возникнет на месте
  const timerOptions = {isPaused, resetKey: purchaseId, enabled: purchaseId !== null};
  usePausableTimeout(() => setIsVisible(true), 30, timerOptions);
  usePausableTimeout(() => setIsVisible(false), 30 + MATCH_POLICY_TOAST_DURATION_MS, timerOptions);
  usePausableTimeout(
    () => dispatch(hidePolicyPurchaseToast()),
    30 + MATCH_POLICY_TOAST_DURATION_MS + SLIDE_DURATION_MS,
    timerOptions
  );

  if (!purchase || isStale || isPaused) return null;

  // Именно find, а не getPlayerById: тот подставляет нейтрального игрока, и
  // покупка неизвестного участника показалась бы тостом «нейтральный игрок
  // купил политику»
  const player = players.find((item) => item.id === purchase.playerId);
  if (!player) return null;

  return (
    // Обертка — под тень плиты со срезанными углами (см. Toast.js)
    <div className={'toast-shadow'}>
      <PolicyPurchaseToast
        className={`match-policy-toast${isVisible ? ' match-policy-toast_visible' : ''}`}
        player={player}
        policy={purchase.policy}
      />
    </div>
  );
};

export default MatchPolicyToast;
